import { config } from '@/config';
import { ROUTES } from '@/types/routes';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { generateCsrfToken } from './security';
import api, { endSession } from '@/client/axiosInstance';
import { queryClient } from '@/client/queryClient';
import { referenceItem } from '@/pages/sign-up';
import { detectTimezone } from '../helpers/dateTimeHelper';
import { reconnectSocket, disconnectSocket } from '@/client/socket';
import { setAccessToken } from '@/client/tokenStorage';

export const login = async (
  email: string,
  password: string,
  keepLogin = true,
) => {
  try {
    const timeZone = detectTimezone();
    const response = await axios.post(
      `${config.VITE_BE_ENDPOINT}auth/login`,
      {
        email,
        password,
        timeZone,
      },
    );

    // TCX-790: wipe any prior account's cached data before adopting the new
    // identity, so a same-tab logout -> login as a different user can never
    // serve the previous user's profile/chats/payment-methods/notifications.
    queryClient.clear();

    // TCX-824: write the access token through the single choke-point so the
    // stored token's subject is tracked for the cross-tab account-switch
    // reload (see client/tokenStorage.ts).
    setAccessToken(response.data.accessToken);
    if (keepLogin) {
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    await generateCsrfToken();
    // Re-handshake the socket so it authenticates as the just-logged-in user.
    reconnectSocket();
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const forgotPassword = async (email: string): Promise<void> => {
  try {
    const response = await axios.post(
      `${config.VITE_BE_ENDPOINT}auth/forgot-password`,
      {
        email,
      },
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const validateForgetPasswordToken = async (
  email: string,
  code: string,
) => {
  try {
    const response = await axios.post(
      `${config.VITE_BE_ENDPOINT}auth/validate-forgot-password-token`,
      {
        email,
        code,
      },
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const resetPassword = async (
  email: string,
  code: string,
  newPassword: string,
): Promise<void> => {
  try {
    const response = await axios.patch(
      `${config.VITE_BE_ENDPOINT}auth/forgot-password`,
      {
        email,
        code,
        newPassword,
      },
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const getActivationConsultantInfo = async (
  token: string | null,
): Promise<{
  token: string;
  email: string;
  bio: string;
  groups: { id: string; name: string }[]; // TCX-507: populated Group refs
  firstName: string;
  lastName: string;
  role: string;
  profilePhoto: string;
}> => {
  try {
    const response = await axios.get(
      `${config.VITE_BE_ENDPOINT}auth/activate-consultant`,
      {
        params: {
          token,
        },
      },
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching activation consultant info');
    return {
      token: '',
      email: '',
      bio: '',
      groups: [],
      firstName: '',
      lastName: '',
      role: '',
      profilePhoto: '',
    };
  }
};

export const getActivationFranchisorInfo = async (
  token: string | null,
): Promise<{
  token: string;
  company: string;
  established: string;
  franchisedUnits?: number;
  category: string[];
  website: string;
  profilePhoto: string;
  email: string;
  corporateAddress: string;
  role: string;
  isActivated: boolean;
  activatedEmail?: string;
}> => {
  try {
    const response = await axios.get(
      `${config.VITE_BE_ENDPOINT}auth/activate-franchisor`,
      {
        params: {
          token,
        },
      },
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching activation franchisor info');
    return {
      token: '',
      company: '',
      established: '',
      category: [],
      website: '',
      profilePhoto: '',
      email: '',
      corporateAddress: '',
      role: '',
      isActivated: false,
    };
  }
};

export const activateConsultant = async (
  token: string,
  password?: string,
  rate?: number,
  bookingLink?: string,
  smsConsent?: boolean,
  references?: referenceItem[],
  isRefPassed?: boolean,
) => {
  if (!password || !rate) {
    throw new Error('Password, rate, and booking link are required');
  }
  try {
    const response = await axios.post(
      `${config.VITE_BE_ENDPOINT}auth/activate-consultant`,
      {
        token,
        password,
        rate,
        bookingLink,
        smsConsent,
        references,
        isRefPassed,
      },
    );
    // TCX-790: activation logs the user in — clear cache on identity change.
    queryClient.clear();
    // TCX-824: write through the single choke-point so the token's subject is
    // tracked for the cross-tab account-switch reload.
    setAccessToken(response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    // Re-handshake the socket so it authenticates as the newly-activated user.
    reconnectSocket();

    return response.data;
  } catch (error) {
    handleError(error, 'Error activating consultant');
    return {
      token: '',
      email: '',
      address: '',
      groups: '',
      firstName: '',
      lastName: '',
      role: '',
    };
  }
};

export const activateFranchisor = async (
  token: string,
  {
    password,
    phone,
    workPhone,
    smsConsent,
  }: {
    password: string;
    phone?: string;
    workPhone?: string;
    smsConsent?: boolean;
  },
) => {
  console.log('activateFranchisor consent at api', smsConsent);
  try {
    const response = await axios.post(
      `${config.VITE_BE_ENDPOINT}auth/activate-franchisor`,
      {
        token,
        password,
        phone,
        workPhone,
        smsConsent,
      },
    );
    // TCX-790: activation logs the user in — clear cache on identity change.
    queryClient.clear();
    // TCX-824: write through the single choke-point so the token's subject is
    // tracked for the cross-tab account-switch reload.
    setAccessToken(response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    // Re-handshake the socket so it authenticates as the newly-activated user.
    reconnectSocket();

    return response.data;
  } catch (error) {
    handleError(error, 'Error activating franchisor');
    return {
      token: '',
      email: '',
      address: '',
      groups: '',
      firstName: '',
      lastName: '',
      role: '',
    };
  }
};

export const uploadImage = async (file: File) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${config.VITE_BE_ENDPOINT}auth/s3-upload-image`,
      formData,
    );

    return response.data.url;
  } catch (error) {
    handleError(error, 'Error uploading image');
  }
};

export const logout = async (): Promise<void> => {
  // Capture the refresh token BEFORE tearing down so we can clear storage
  // synchronously (below) and still tell the server which token to revoke.
  const refreshToken = localStorage.getItem('refreshToken');

  // TCX-838: end the session and clear ALL local state SYNCHRONOUSLY, before any
  // await. If storage were cleared only after the awaited logout POST, a request
  // that started during that window would read the still-present tokens (and
  // attach a bearer), and its response could write tokens back afterwards,
  // resurrecting the just-logged-out session. endSession() also bumps the
  // generation (so in-flight responses are treated as stale) and drops the
  // in-memory Authorization default.
  endSession();
  localStorage.clear();
  sessionStorage.clear();
  // TCX-790: also drop the in-memory React Query cache so a same-tab login as a
  // different user can't serve the previous user's cached data.
  queryClient.clear();
  // TCX-820: drop the authenticated socket and keep it down so a stale in-flight
  // refresh response can't revive it as the just-logged-out user.
  disconnectSocket();

  try {
    if (refreshToken) {
      // Raw axios (not `api`): /auth/logout is excluded from the token / CSRF
      // middleware (app.module), so it needs only the refresh token in the body.
      // Going through `api` after clearing storage would trigger a CSRF-token
      // fetch that now 401s. This revokes the refresh token server-side.
      await axios.post(
        `${config.VITE_BE_ENDPOINT}auth/logout`,
        { refreshToken },
        { headers: { 'ngrok-skip-browser-warning': true } },
      );
    }
  } catch (error) {
    // Local teardown already happened; a failed server call can't un-log-out.
    console.error('Logout error:', error);
  }
};

export const getBrandNames = async () => {
  try {
    const response = await axios.get(
      `${config.VITE_BE_ENDPOINT}auth/get-brand-names`,
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching brand names');
  }
};

export const validateBrandName = async (brandName: string) => {
  try {
    const response = await axios.post(
      `${config.VITE_BE_ENDPOINT}auth/get-brand-names`,
      { brandName },
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error validating brand name');
  }
};

export const sendBetaTestingRequest = async (
  email: string,
  details?: string,
  firstName?: string,
  lastName?: string,
  linkedin?: string,
  phone?: string,
  leadSource?: string,
) => {
  try {
    const response = await axios.post(
      `${config.VITE_BE_ENDPOINT}auth/beta-testing-request`,
      {
        email,
        details,
        firstName,
        lastName,
        linkedin,
        phone,
        leadSource,
      },
    );
    return response.data;
  } catch (error) {
    handleError(error, 'Error sending beta testing request');
  }
};

const handleError = (error: unknown, message: string): never => {
  if (axios.isAxiosError(error)) {
    throw new Error(
      `${message}: ${error.response?.data?.message || error.message}`,
    );
  } else {
    throw new Error(`${message}: Unknown error occurred`);
  }
};

// SEC-04 (TCX-750): initiating the Cronofy connect flow is now an authenticated
// request. The backend derives the consultant from the JWT and returns the OAuth
// URL (carrying a signed, user-bound `state`); the caller then performs the
// top-level browser redirect. No `consultantId` is sent from the client.
export const getCronofyConnectUrl = async (): Promise<string> => {
  const response = await api.get<{ url: string }>(
    `${config.VITE_BE_ENDPOINT}auth/consultant/connect-calendar`,
  );
  return response.data.url;
};

export const disconnectCalendar = async (
  id: string | undefined,
): Promise<AxiosResponse<{ success: boolean }>> => {
  try {
    const response = await api.post(
      `${config.VITE_BE_ENDPOINT}auth/consultant/disconnect-calendar`,
      {
        consultantId: id,
      },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message ?? error.message);
    }

    throw new Error('An unexpected error occurred');
  }
};
